import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Sparkle } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail, streamAi } from "@/lib/api";

const MODEL_LABELS = {
  "gpt-5.4": "ChatGPT GPT-5.4 — strongest writing",
  "gpt-5.4-mini": "ChatGPT GPT-5.4 mini — quick and cheap",
  "gemini-3-flash-preview": "Gemini 3 Flash — fast and cheapest",
  "gemini-3.1-pro-preview": "Gemini 3.1 Pro — slower, best reasoning",
};

const Row = ({ label, hint, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 py-4 last:border-0">
    <div className="min-w-0">
      <div className="text-sm font-bold text-zinc-200">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
    </div>
    {children}
  </div>
);

export default function AiAssistantSettings() {
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState({});
  const [keyReady, setKeyReady] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trying, setTrying] = useState(false);
  const [sample, setSample] = useState("");

  useEffect(() => {
    api
      .get("/settings/ai-text")
      .then(({ data }) => {
        setCfg(data.config);
        setUsage(data.usage || {});
        setKeyReady(data.key_ready);
      })
      .catch(() => toast.error("Could not load the AI settings"));
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/ai-text", cfg);
      setCfg(data.config);
      setUsage(data.usage || {});
      toast.success("Saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setSaving(false);
  };

  const tryIt = async () => {
    setTrying(true);
    setSample("");
    let out = "";
    try {
      await streamAi("/ai/chat", { message: "In one sentence, what is in stock right now?" }, (d) => {
        out += d;
        setSample(out);
      });
      const { data } = await api.get("/ai/usage");
      setUsage(data);
    } catch (err) {
      toast.error(err.message || "The model did not answer");
    }
    setTrying(false);
  };

  if (!cfg)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="AI Assistant" />
        <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
      </div>
    );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="AI Assistant" />
      <main className="relative z-10 pb-28 pt-6" data-testid="ai-text-page">
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Settings
        </div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          AI Assistant
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          A shop assistant that answers from your live stock, and a Draft a
          reply button on enquiries. Runs on Gemini or ChatGPT — your choice,
          with caps so you control the spend.
        </p>

        {!keyReady && (
          <div
            className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200"
            data-testid="ai-text-key-warning"
          >
            No AI key is configured on the server, so nothing will answer.
          </div>
        )}

        <div className="mt-6 grid gap-4 lg:grid-cols-[1.35fr_1fr] lg:items-start">
          <div className="space-y-4">
            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="ai-text-switches"
            >
              <Row
                label="AI assistant"
                hint="Master switch for everything on this page"
              >
                <Switch
                  data-testid="ai-text-enabled"
                  checked={cfg.enabled}
                  onCheckedChange={(v) => set({ enabled: v })}
                />
              </Row>
              <Row
                label="Shop assistant chat"
                hint="The Assistant page in the sidebar, reads your live stock"
              >
                <Switch
                  data-testid="ai-text-assistant"
                  checked={cfg.assistant}
                  onCheckedChange={(v) => set({ assistant: v })}
                />
              </Row>
              <Row
                label="Reply drafting"
                hint="Draft a reply, follow-up and text message on enquiries"
              >
                <Switch
                  data-testid="ai-text-drafting"
                  checked={cfg.drafting}
                  onCheckedChange={(v) => set({ drafting: v })}
                />
              </Row>
              <Row
                label="Import by supplier link"
                hint="Reads a supplier's page and fills in slabs or sinks for you to approve"
              >
                <Switch
                  data-testid="ai-text-importing"
                  checked={cfg.importing}
                  onCheckedChange={(v) => set({ importing: v })}
                />
              </Row>
              <Row label="Model" hint="Gemini is cheaper, ChatGPT writes warmer">
                <select
                  data-testid="ai-text-model"
                  value={cfg.model}
                  onChange={(e) => set({ model: e.target.value })}
                  className="h-10 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                >
                  {Object.entries(MODEL_LABELS).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </Row>
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="ai-text-limits"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                Usage limits
              </div>
              <Row label="Answers per day">
                <Input
                  data-testid="ai-text-daily"
                  type="number"
                  min="0"
                  value={cfg.daily_limit}
                  onChange={(e) =>
                    set({ daily_limit: Number(e.target.value) || 0 })
                  }
                  className="h-10 w-28 border-zinc-800 bg-zinc-950 text-sm"
                />
              </Row>
              <Row label="Answers per month">
                <Input
                  data-testid="ai-text-monthly"
                  type="number"
                  min="0"
                  value={cfg.monthly_limit}
                  onChange={(e) =>
                    set({ monthly_limit: Number(e.target.value) || 0 })
                  }
                  className="h-10 w-28 border-zinc-800 bg-zinc-950 text-sm"
                />
              </Row>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="ai-text-usage">
                {[
                  ["Today", usage.today],
                  ["This month", usage.month],
                  ["All time", usage.total],
                ].map(([label, n]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {label}
                    </div>
                    <div className="font-mono-data mt-1 text-lg text-zinc-100">
                      {n ?? 0}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                How it should write
              </div>
              <div className="mt-4 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  House voice
                </div>
                <Textarea
                  data-testid="ai-text-voice"
                  rows={3}
                  value={cfg.voice}
                  onChange={(e) => set({ voice: e.target.value })}
                  className="border-zinc-800 bg-zinc-950 text-sm"
                />
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Sign-off for drafted emails
                </div>
                <Textarea
                  data-testid="ai-text-signature"
                  rows={3}
                  value={cfg.signature}
                  onChange={(e) => set({ signature: e.target.value })}
                  placeholder={"Kind regards,\nBrioni Granite\n(314) 000-0000"}
                  className="border-zinc-800 bg-zinc-950 text-sm"
                />
              </div>
            </section>

            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="ai-text-save"
                disabled={saving}
                onClick={save}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <FloppyDisk size={15} weight="bold" className="mr-2" />
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                data-testid="ai-text-try"
                variant="outline"
                disabled={trying || !cfg.enabled}
                onClick={tryIt}
                className="h-11 border-zinc-800 bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                <Sparkle size={15} weight="bold" className="mr-2" />
                {trying ? "Asking…" : "Ask it something"}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
              Test answer
            </div>
            <div
              className="mt-3 min-h-[140px] whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-300"
              data-testid="ai-text-sample"
            >
              {sample || (trying ? "…" : "Nothing asked yet.")}
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              Every answer is billed to your Emergent key. The assistant only
              ever reads your own data — stock, enquiries and shop details.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
